import React, { useState, useEffect } from 'react';
import useStyles from './styles';
import Form from 'global/components/form';
import { get, size, set, isEqual } from 'global/lib/lodash';
import { Paper, Button, Icon, LinearProgress, IconButton, InputLabel, FormHelperText, Box } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import sendData from 'redux/actions/sendData';
import useRetrieveData from 'global/hooks/useRetrieveData';
import clsx from 'clsx';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { FullScreen } from 'global/components/modal';
import Droprealms, { Access } from 'global/components/droprealms';
import { allRealmsForUserRealm, getBase64Image, parseJson, toBase64, getUserAppPermissions, appPermissionToServer, _size } from 'global/utils';
import { MAXSIZE } from 'global/components/drop-area';
import { getViewStyle, setViewStyle } from 'redux/actions/view';
import PermissionGate from 'global/components/permission-gate';
import { getUserPermissions } from 'redux/actions/userLogin';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';
import { useScreen } from 'global/styles';

export const SETTINGS = {
  logo: 'Logo',
  primaryColor: 'Primary Color',
  secondaryColor: 'Secondary Color',
  selfRegistration: 'Self Registration',
  accessRestrictions: 'Self Registration Access Restrictions',
  allowedDomains: 'Self Registration Allowed Domains',
  assessmentAccess: 'Assessment Default General Access',
  assessmentScorecard: 'Assessment Default Scorecard',
  assessmentColorScale: 'Assessment Default Color Scale',
  defaultPermissions: 'Builder Default Permissions',
};

const COLORKEYS = [SETTINGS.primaryColor, SETTINGS.secondaryColor];

export default PermissionGate((props) => {
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [formState, setFormState] = useState({});
  const [error, setError] = useState();
  const { me } = useUser();
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const [accessModal, setAccessModal] = useState(false);
  const [allRealms, setAllRealms] = useState([]);
  const [settings, setSettings] = useState({});
  const dispatch = useDispatch();
  const [ac, setAc] = useState({});
  const { filterPermissionsByNav } = useNavSettings();

  const aSettings = useRetrieveData({ url: `/account-settings` }, { build: ['accountSettings'] });
  const { data, loading, update } = useRetrieveData({ url: `/realms?realmPicker=1` }, { build: ['realms'] });
  const scorecards = useRetrieveData({ url: '/scorecards' }, { build: ['scorecards'], store: 'data.scorecards' });

  const userRealm = get(me, 'realm');

  useEffect(() => {
    (async () => {
      try {
        if (!aSettings.loading && !loading) {
          setHideForm(true);

          let aRealms = data;
          setAllRealms(allRealmsForUserRealm(data, me.realm));

          const obj = {};
          const toParse = [SETTINGS.accessRestrictions, SETTINGS.allowedDomains, SETTINGS.defaultPermissions, SETTINGS.assessmentColorScale];
          await Promise.all(
            aSettings.data.map(async (setting) => {
              const value = async () => {
                if (COLORKEYS.indexOf(setting.name) > -1) {
                  return setting.value.replace('#', '');
                } else if (setting.name === SETTINGS.logo) {
                  if (setting.value.indexOf('https://perform-3-media-dev.s3.amazonaws.com/local/medialibrary/456291/perform_logo_white.png') > -1) {
                    setting.value = 'https://perform-3-media-dev.s3.amazonaws.com/local/medialibrary/456291/perform_logo_white.png';
                  }
                  const val = await getBase64Image(setting.value);
                  return `data:image/png;base64,${val}`;
                } else if (toParse.indexOf(setting.name) > -1) {
                  return parseJson(setting.value);
                } else {
                  return setting.value;
                }
              };
              const val = await value();
              set(obj, setting.name, val);
            })
          );

          const userPerms = getUserAppPermissions(get(obj, SETTINGS.defaultPermissions, []).map((p) => ({ name: p })));
          const access = {};
          (userPerms || []).map((up) => set(access, up.value, true));
          setAc(access);

          setSettings(obj);
          const realmIds = get(obj, SETTINGS.accessRestrictions);
          if (size(realmIds)) {
            setFormState({
              ...formState,
              [SETTINGS.accessRestrictions]: realmIds.map((id) => aRealms.find((r) => r.id == id)).filter((r) => r),
            });
          }
          setHideForm(false);
        }
      } catch (err) {
        console.error({ err });
        setHideForm(false);
      }
    })();
  }, [aSettings.loading, loading]);

  useEffect(() => {
    dispatch(
      setViewStyle({
        primaryColor: get(formState, SETTINGS.primaryColor) ? `#${get(formState, SETTINGS.primaryColor)}` : undefined,
        secondaryColor: get(formState, SETTINGS.secondaryColor) ? `#${get(formState, SETTINGS.secondaryColor)}` : undefined,
      })
    );
  }, [get(formState, SETTINGS.primaryColor), get(formState, SETTINGS.secondaryColor)]);

  const toggleEdit = () => {
    setCanEdit(!canEdit);
  };

  const formatData = async () => {
    if (!get(formState, 'name')) {
      throw new Error("Name can't be blank");
    }
    const realmData = {
      data: {
        type: 'realms',
        id: get(userRealm, 'id'),
        attributes: {
          name: get(formState, 'name', ''),
        },
      },
    };

    const settingsData = (
      await Promise.all(
        Object.keys(settings).map(async (k) => {
          const origSetting = settings[k];
          const formSetting = get(formState, k);
          const reqSetting = aSettings.data.find((a) => get(a, 'name') === k);

          if (!isEqual(origSetting, formSetting) && formSetting) {
            const _attributes = async () => {
              if (COLORKEYS.indexOf(k) > -1) {
                return {
                  value: `#${formSetting}`,
                };
              } else if (k === SETTINGS.logo) {
                const val = get(formSetting, '0');
                const upload = await toBase64(val);

                if (upload === get(settings, k)) {
                  //dont update the image if it never changed
                  return null;
                }

                return {
                  value: `${get(val, 'name', '').replace('.png')}.png`,
                  upload,
                };
              } else if (k === SETTINGS.accessRestrictions) {
                return {
                  value: JSON.stringify((formSetting || []).filter((f) => f).map((r) => r.id)),
                };
              } else if (k === SETTINGS.allowedDomains) {
                return {
                  value: JSON.stringify(formSetting),
                };
              } else if (k === SETTINGS.defaultPermissions) {
                return {
                  value: JSON.stringify(
                    appPermissionToServer(
                      Object.keys(formSetting)
                        .filter((k) => formSetting[k])
                        .map((k) => k)
                    )
                  ),
                };
              } else if (k === SETTINGS.assessmentColorScale) {
                return { value: JSON.stringify(formSetting) };
              } else {
                return {
                  value: formSetting,
                };
              }
            };

            const attributes = await _attributes();

            if (!attributes) {
              return null;
            }

            const override = get(reqSetting, 'override');
            const account_setting_id = get(reqSetting, 'id', '').toString();
            const overrideId = (get(reqSetting, 'overrideId') || '').toString();
            set(attributes, 'account_setting_id', account_setting_id);
            set(attributes, 'realm_id', get(userRealm, 'id', '').toString());
            return {
              data: {
                id: override === 1 ? overrideId : undefined,
                type: 'realm-account-settings',
                attributes,
              },
            };
          }
        })
      )
    ).filter((f) => f);

    await Promise.all(
      (settingsData || []).map(async (req) => {
        const id = get(req, 'data.id');
        const method = id ? 'patch' : 'post';
        await sendData({
          url: `/realm-account-settings${method === 'patch' ? `/${id}` : ''}`,
          method,
          data: req,
        });
      })
    );

    await sendData({
      url: `/realms/${get(userRealm, 'id')}`,
      method: 'patch',
      data: realmData,
    });
  };

  const reload = async () => {
    setFormState({});
    await Promise.all([await aSettings.update(), await update(), await dispatch(getViewStyle()), await dispatch(getUserPermissions())]);
  };

  const formSubmit = async () => {
    try {
      setError();
      setFormLoading(true);
      await formatData();
      setCanEdit(false); //this forces reload on the canEdit effect
      setFormLoading(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const onFormState = (vals) => {
    setFormState({ ...formState, ...vals });
  };

  const reset = async () => {
    setHideForm(true);
    await reload();
    setHideForm(false);
  };

  useEffect(() => {
    if (!canEdit && !loading) {
      reset();
    }
  }, [canEdit]);

  const onUpdateAccess = (realms) => {
    const resRealms = Object.keys(realms).map((k) => realms[k]);
    setFormState({
      ...formState,
      [SETTINGS.accessRestrictions]: resRealms,
    });
  };

  const toggleAccess = () => {
    setAccessModal(!accessModal);
  };

  const divisions = allRealms.filter((r) => get(r, 'tier') === 'division');
  const communitys = allRealms.filter((r) => get(r, 'tier') === 'community');

  const renderLocations = () => {
    return (
      <div>
        <div>
          <div className={classes.locRow}>
            <DivisionIcon className={classes.locIcon} />
            {`${size(divisions)} Divisions`}
          </div>
          <div className={classes.locRow}>
            <CommunityIcon className={classes.locIcon} />
            {`${size(communitys)} Communities`}
          </div>
        </div>

        {canEdit && (
          <Button variant="contained" startIcon={<Icon>location_on</Icon>} onClick={toggleAccess} color={'secondary'}>
            MANAGE
          </Button>
        )}
      </div>
    );
  };

  const accessRestrictions = get(formState, SETTINGS.accessRestrictions, get(settings, SETTINGS.accessRestrictions, []));

  return aSettings.loading || loading || hideForm || !_size(settings) ? (
    <LinearProgress />
  ) : (
    <div className={classes.container}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <div className={classes.buttonContainer}>
        {!formLoading && (
          <Button color={'secondary'} variant="contained" onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
            {'Edit'}
          </Button>
        )}
      </div>

      <Form
        loading={formLoading}
        showAllScreens={true}
        formStyle={classes.formStyle}
        onSubmit={formSubmit}
        canSubmit={canEdit}
        onFormState={onFormState}
        submitText="Save Changes"
        screens={[
          [
            {
              title: 'GENERAL INFO',
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobile ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                props: {
                  className: classes.screenWidth,
                },
              },
              inputs: [
                {
                  id: 'name',
                  type: 'TextField',
                  defaultValue: () => get(userRealm, 'name', ''),
                  props: {
                    label: 'Name',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.logo,
                  type: 'DropZone',
                  props: {
                    label: 'Logo',
                    disabled: !canEdit,
                    filesLimit: 1,
                    initialFiles: [get(settings, SETTINGS.logo, '')],
                    acceptedFiles: ['image/*'],
                    maxFileSize: MAXSIZE,
                    helperText: !!canEdit && 'Drag and drop an image file here or click container',
                  },
                },
                {
                  id: SETTINGS.primaryColor,
                  type: 'ColorSquare',
                  defaultValue: () => get(settings, SETTINGS.primaryColor, ''),
                  props: {
                    label: 'Primary Color',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.secondaryColor,
                  type: 'ColorSquare',
                  defaultValue: () => get(settings, SETTINGS.secondaryColor, ''),
                  props: {
                    label: 'Secondary Color',
                    disabled: !canEdit,
                  },
                },
              ],
            },
            {
              title: 'ASSESSMENT DEFAULTS',
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobile ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(classes.screenWidth, classes.accessContainer),
                },
              },
              inputs: [
                {
                  id: SETTINGS.assessmentAccess,
                  type: 'ToggleButton',
                  options: [
                    { title: 'NORMAL', value: 'normal' },
                    { title: 'GLOBAL', value: 'global' },
                    { title: 'HIDDEN', value: 'hidden' },
                  ],
                  defaultValue: () => get(settings, SETTINGS.assessmentAccess, 'normal'),
                  props: {
                    exclusive: true,
                    label: 'General Visibility',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentScorecard,
                  type: 'Select',
                  defaultValue: () => get(settings, SETTINGS.assessmentScorecard, 0),
                  data: scorecards.data.map((card) => ({ title: get(card, 'name'), value: get(card, 'id') })),
                  props: {
                    label: 'Default Scorecard',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentColorScale,
                  type: 'ColorSlider',
                  defaultValue: () => get(settings, SETTINGS.assessmentColorScale, [0, 100]),
                  props: {
                    label: 'Color Scale',
                    disabled: !canEdit,
                  },
                },
              ],
            },
          ],
          [
            {
              title: 'LOCATIONS',
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobile ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: classes.accessContainer,
                },
              },
              inputs: [{ component: renderLocations() }],
            },
            {
              title: 'SELF REGISTRATION',
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobile ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(classes.accessContainer),
                },
              },
              inputs: [
                {
                  id: SETTINGS.selfRegistration,
                  type: 'RadioButton',
                  defaultValue: () => get(settings, SETTINGS.selfRegistration),
                  options: [
                    { value: '1', label: 'Enabled' },
                    { value: '0', label: 'Disabled' },
                  ],
                  props: {
                    label: 'Self-Registration Status',
                    disabled: !canEdit,
                    className: classes.flexRow,
                  },
                },
                {
                  id: SETTINGS.allowedDomains,
                  type: 'ChipAdd',
                  defaultValue: () => [...get(settings, SETTINGS.allowedDomains, [])],
                  props: {
                    label: 'Allowed Domains',
                    disabled: !canEdit,
                  },
                },
                {
                  component: (
                    <div className={classes.fullWidth}>
                      <InputLabel shrink={false} className={classes.toggleLabel}>
                        Access Restrictions
                      </InputLabel>
                      <Droprealms
                        canBackOutOfBuilder={false}
                        alwaysShow
                        showModify={canEdit}
                        onUpdate={onUpdateAccess}
                        realms={accessRestrictions}
                        title={size(accessRestrictions) === 0 ? 'None' : undefined}
                      />
                    </div>
                  ),
                },
                {
                  id: SETTINGS.defaultPermissions,
                  type: 'Checkbox',
                  defaultValue: () => ac,
                  options: filterPermissionsByNav().map((up) => ({ ...up, label: up.title })),
                  props: {
                    label: 'Default Permissions',
                    disabled: !canEdit,
                  },
                },
                {
                  component: () => {
                    const show = get(formState, SETTINGS.selfRegistration) !== '0';
                    const link = `${window.location.origin}/register/${get(userRealm, 'id')}`;

                    return !show ? null : (
                      <FormHelperText>
                        <Box className="boxContainer">
                          <Box>{'New or existing users can use these links to configure their account. There are three different tiers for self-registration:'}</Box>
                          <Box style={{ height: 10 }} />
                          <Box>
                            {`Corporate users: `}
                            <a target={'_blank'} href={`${link}/builder`}>{`${link}/builder`}</a>
                          </Box>
                          <Box>
                            {`Divisional users: `}
                            <a target={'_blank'} href={`${link}/division`}>{`${link}/division`}</a>
                          </Box>
                          <Box>
                            {`Community users: `}
                            <a target={'_blank'} href={`${link}/community`}>{`${link}/community`}</a>
                          </Box>
                        </Box>
                      </FormHelperText>
                    );
                  },
                },
              ],
            },
          ],
        ]}
      />

      <FullScreen
        centerContent
        onClose={toggleAccess}
        title={
          <>
            <Icon style={{ marginRight: 5 }}>location_on</Icon> Manage Locations
          </>
        }
        open={accessModal}
      >
        <Access canAdd={true} tiersCanAdd={['builder', 'division', 'community']} deselectOnChange={true} realms={[]} editable={true} showKey />
      </FullScreen>
    </div>
  );
});
